import React, { useState, useEffect } from 'react';
import { videoGroups, videoUrls } from './VideoConfig';
import Sallie from './Sallie';
import customizeMessages from "../lib/customizeMessages";
import { useTranslation } from "../i18n/TranslationProvider";
import { translateOr } from "../i18n/translateData";
import { Row, Col, Collapse } from "react-bootstrap";
import { getPatientExercises } from '../api/adminRequests';
import './VideoChecklist.css';

// Individual videos that are always accessible regardless of assigned exercises.
// These are equipment/setup/intro videos that every patient may need.
const ALWAYS_ACCESSIBLE_VIDEO_IDS = new Set([
    'primitive-reflex-pr-intro-home',
    'oculomotor-okn-drum',
    'metrotimer-metro-intro',
    'sensory-equipment-galvanic',
    'sensory-equipment-bone-conduction',
    'sensory-equipment-tens',
    'transcranial-trans-brain-driver',
    'transcranial-trans-saline',
    'transcranial-trans-level-select',
    'pons-pons-intro',
    'pons-pons-practical',
    'photobiomodulation-laser-intro',
    'photobiomodulation-laser-setup',
]);

const Video = ({ user }) => {
    const { t } = useTranslation();
    const [expandedSections, setExpandedSections] = useState({});
    const [completedVideos, setCompletedVideos] = useState({});
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [assignedExercises, setAssignedExercises] = useState({});
    const [loadingExercises, setLoadingExercises] = useState(true);
    // null = no restriction; Set<string> when arriving from Dashboard "Video" button
    const [accessibleVideoIds, setAccessibleVideoIds] = useState(null);
    
    useEffect(() => {
        if (user.userType !== "provider") {
            setLoadingExercises(true);
            getPatientExercises()
                .then(({ data }) => {
                    setAssignedExercises(data || {});
                    setLoadingExercises(false);
                })
                .catch((error) => {
                    console.error("Error fetching assigned exercises:", error);
                    setLoadingExercises(false);
                });
        } else {
            setLoadingExercises(false);
        }
    }, [user.userType]);
    
    useEffect(() => {
        const saved = localStorage.getItem('completedVideos');
        if (saved) {
            setCompletedVideos(JSON.parse(saved));
        }
    }, []);
    
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';
        script.async = true;
        document.body.appendChild(script);
        
        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);
    
    // Handle URL parameters - select video from Dashboard.js "Video" button
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const videoIdFromUrl = urlParams.get('id');   // single exercise (small button)
        const videoIdsFromUrl = urlParams.get('ids');  // all exercises (big button / header tab)

        // Helper: given one video ID, add it + next 2 levels to the accessible set
        const addExerciseWindow = (videoId, ids) => {
            const exerciseInfo = getExerciseFromVideoId(videoId);
            if (exerciseInfo && exerciseInfo.slug && exerciseInfo.level !== null) {
                const { slug, level: originalLevel } = exerciseInfo;
                for (let offset = 0; offset <= 2; offset++) {
                    const vid = getVideoIdForSlugAndLevel(slug, originalLevel + offset);
                    if (vid) ids.add(vid);
                }
            } else {
                ids.add(videoId); // fallback: allow exact video if unparseable
            }
        };

        if (videoIdsFromUrl) {
            // Multiple exercises from the big Video Tutorials button — build a combined window
            const ids = new Set();
            videoIdsFromUrl.split(',').forEach(vid => addExerciseWindow(vid.trim(), ids));
            console.log('Accessible video IDs (multi):', [...ids]);
            setAccessibleVideoIds(ids);
            // Select the first video in the list
            const firstId = videoIdsFromUrl.split(',')[0].trim();
            setSelectedVideo(firstId);
        } else if (videoIdFromUrl) {
            console.log('Video ID from URL:', videoIdFromUrl);

            // currentLevelId: only present when the patient has self-progressed above their
            // original assigned level. Dashboard passes the video ID for exercise_level so we
            // can auto-select and expand the correct subgroup (e.g. core-level-4 instead of
            // core-level-3). The accessible window is still anchored to videoIdFromUrl (?id).
            const currentLevelId = urlParams.get('currentLevelId');

            // The video to auto-select and expand: prefer currentLevelId when provided,
            // otherwise fall back to the window-anchor video.
            const videoIdToSelect = currentLevelId || videoIdFromUrl;

            // Set this video as selected (this triggers the blue highlight and auto-play)
            setSelectedVideo(videoIdToSelect);

            // Build the accessible set: original assigned level + next 2 levels.
            // Always anchored to ?id (level_since_last_eval baseline) — never shifts.
            const ids = new Set();
            addExerciseWindow(videoIdFromUrl, ids);
            console.log('Accessible video IDs:', [...ids]);
            setAccessibleVideoIds(ids);

            // Parse videoIdToSelect (not videoIdFromUrl) to expand the correct subgroup.
            // For categories where the group embeds the level (pr-*, core-*), this ensures
            // the right level folder is open even after self-progression.
            // Video IDs follow pattern: {group}-{subgroup}-{item}
            // Examples: "pr-levels-1-pg-1", "core-level-2-cu-2", "balance-bal-3"

            const sectionsToExpand = {};

            if (videoIdToSelect.startsWith('pr-')) {
                // Primitive Reflex videos: pr-levels-1-pg-1, pr-level-4-pg-4, etc.
                sectionsToExpand['primitive-reflex'] = true;

                // Extract subgroup ID (e.g., "pr-levels-1" or "pr-level-4")
                const match = videoIdToSelect.match(/^(pr-levels?-\d+)/);
                if (match) {
                    sectionsToExpand[match[1]] = true;
                }
            } else if (videoIdToSelect.startsWith('core-')) {
                // Core exercises: core-level-1-cu-1, core-level-2-sb-2, etc.
                sectionsToExpand['core-exercises'] = true;

                // Extract subgroup ID (e.g., "core-level-1")
                const match = videoIdToSelect.match(/^(core-level-\d+)/);
                if (match) {
                    sectionsToExpand[match[1]] = true;
                }
            } else if (videoIdToSelect.startsWith('balance-')) {
                // Balance videos: balance-bal-1, balance-bal-2, etc.
                sectionsToExpand['balance'] = true;
            } else if (videoIdToSelect.startsWith('oculomotor-')) {
                // Oculomotor: oculomotor-ocul-1, etc.
                sectionsToExpand['oculomotor'] = true;
            } else if (videoIdToSelect.startsWith('olfactory-')) {
                // Olfactory: olfactory-olf-1, etc. (fixed from olfac to olf)
                sectionsToExpand['olfactory'] = true;
            } else if (videoIdToSelect.startsWith('vestibular-')) {
                // Vestibular: vestibular-vest-1, etc.
                sectionsToExpand['vestibular'] = true;
            } else if (videoIdToSelect.startsWith('interactive-metronome-')) {
                // Interactive Metronome: interactive-metronome-int-metro-1, etc.
                sectionsToExpand['rhythm-timing'] = true;
                sectionsToExpand['interactive-metronome'] = true;
            } else if (videoIdToSelect.startsWith('metrotimer-')) {
                // Metro Timer: metrotimer-metro-1, etc.
                sectionsToExpand['rhythm-timing'] = true;
                sectionsToExpand['metrotimer'] = true;
            } else if (videoIdToSelect.startsWith('transcranial-')) {
                // Transcranial: transcranial-trans-1, etc.
                sectionsToExpand['sensory-equipment'] = true;
                sectionsToExpand['transcranial'] = true;
            } else if (videoIdToSelect.startsWith('pons-')) {
                // PONs Stimulator: pons-pons-1, etc.
                sectionsToExpand['sensory-equipment'] = true;
                sectionsToExpand['pons'] = true;
            } else if (videoIdToSelect.startsWith('left-hemisphere-')) {
                // Photobiomodulation Left: left-hemisphere-left-1, etc.
                sectionsToExpand['sensory-equipment'] = true;
                sectionsToExpand['photobiomodulation'] = true;
                sectionsToExpand['left-hemisphere'] = true;
            } else if (videoIdToSelect.startsWith('right-hemisphere-')) {
                // Photobiomodulation Right: right-hemisphere-right-1, etc.
                sectionsToExpand['sensory-equipment'] = true;
                sectionsToExpand['photobiomodulation'] = true;
                sectionsToExpand['right-hemisphere'] = true;
            }

            console.log('Expanding sections:', sectionsToExpand);
            setExpandedSections(sectionsToExpand);

            // Scroll to video player after a short delay to ensure rendering
            setTimeout(() => {
                handleGoToVideo();
            }, 300);
        }
    }, []); 
    
    const getPossibleMessages = () => ({
        sallieText: {
            Stroke: t("dashboard.sallieDashboard.video"),
            tbiPatient: t("dashboard.sallieDashboard.video"),
            caregiver: t("dashboard.sallieDashboard.video"),
            provider: t("dashboard.sallieDashboard.video"),
        },
    });

    const getMessage = customizeMessages({ user, getPossibleMessages });
    
    // Helper function to translate group/subgroup names
    const getTranslatedGroupName = (groupId, groupName) => {
        // Check if translation exists for this group ID
        // Falls back to the name-based lookup (covers generic "Level N" subgroups)
        return translateOr(t, `video.exerciseGroups.${groupId}`, null) ?? translateExerciseName(groupName);
    };

    // Helper function to translate exercise names
    function translateExerciseName(exerciseName) {
        if (!exerciseName) return exerciseName;
        // Try VIDEO_NAMES first (includes all video names from intro videos to exercises to supplements)
        // Then EXERCISE_NAMES (base names like "Palmer Grasp")
        const exact =
            translateOr(t, `video.videoNames.${exerciseName}`, null) ??
            translateOr(t, `video.exerciseNames.${exerciseName}`, null);
        if (exact) return exact;
        // Fallback: translate the standalone word "Level" in any untranslated title (e.g. "Level 3")
        return exerciseName.replace(/\bLevel\b/g, t("video.ui.level"));
    }
    
    const handleGoToVideo = () => {
        const videoCol = document.querySelector('.video-content-col');
        if (videoCol) {
            videoCol.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };
    
    const getVideoTitle = (videoId) => {
        if (!videoId) return null;
        
        let foundTitle = null;
        
        videoGroups.forEach(group => {
            if (group.items) {
                const item = group.items.find(item => `${group.id}-${item.id}` === videoId);
                if (item) {
                    foundTitle = item.name;
                    return;
                }
            }
            
            if (group.subgroups) {
                group.subgroups.forEach(subgroup => {
                    if (subgroup.items) {
                        const item = subgroup.items.find(item => `${subgroup.id}-${item.id}` === videoId);
                        if (item) {
                            foundTitle = item.name;
                            return;
                        }
                    }
                    
                    if (subgroup.subgroups) {
                        subgroup.subgroups.forEach(nestedSubgroup => {
                            if (nestedSubgroup.items) {
                                const item = nestedSubgroup.items.find(item => `${nestedSubgroup.id}-${item.id}` === videoId);
                                if (item) {
                                    foundTitle = item.name;
                                }
                            }
                        });
                    }
                });
            }
            
            if (group.additionalItems) {
                const item = group.additionalItems.find(item => `${group.id}-${item.id}` === videoId);
                if (item) {
                    foundTitle = item.name;
                }
            }
        });
        
        return foundTitle;
    };
    
    const getAllVideosInGroup = (group) => {
        const videos = [];
        
        if (group.items) {
            group.items.forEach(item => {
                videos.push(`${group.id}-${item.id}`);
            });
        }
        
        if (group.subgroups) {
            group.subgroups.forEach(subgroup => {
                if (subgroup.items) {
                    subgroup.items.forEach(item => {
                        videos.push(`${subgroup.id}-${item.id}`);
                    });
                }
                if (subgroup.subgroups) {
                    subgroup.subgroups.forEach(nestedSubgroup => {
                        nestedSubgroup.items.forEach(item => {
                            videos.push(`${nestedSubgroup.id}-${item.id}`);
                        });
                    });
                }
            });
        }
        
        if (group.additionalItems) {
            group.additionalItems.forEach(item => {
                videos.push(`${group.id}-${item.id}`);
            });
        }
        
        return videos;
    };
    
    const isGroupCompleted = (group) => {
        const groupVideos = getAllVideosInGroup(group);
        return groupVideos.every(videoId => completedVideos[videoId]);
    };
    
    const getCurrentGroupIndex = () => {
        for (let i = 0; i < videoGroups.length; i++) {
            if (!isGroupCompleted(videoGroups[i])) {
                return i;
            }
        }
        return videoGroups.length - 1;
    };
    
    const getAllVideosInGroupInOrder = (group) => {
        const videos = [];
        
        if (group.items) {
            group.items.forEach(item => {
                videos.push({ id: `${group.id}-${item.id}`, name: item.name });
            });
        }
        
        if (group.subgroups) {
            group.subgroups.forEach(subgroup => {
                if (subgroup.items) {
                    subgroup.items.forEach(item => {
                        videos.push({ id: `${subgroup.id}-${item.id}`, name: item.name });
                    });
                }
                if (subgroup.subgroups) {
                    subgroup.subgroups.forEach(nestedSubgroup => {
                        nestedSubgroup.items.forEach(item => {
                            videos.push({ id: `${nestedSubgroup.id}-${item.id}`, name: item.name });
                        });
                    });
                }
            });
        }
        
        if (group.additionalItems) {
            group.additionalItems.forEach(item => {
                videos.push({ id: `${group.id}-${item.id}`, name: item.name });
            });
        }
        
        return videos;
    };
    
    const getCurrentVideoIndexInGroup = (groupIndex) => {
        const group = videoGroups[groupIndex];
        const groupVideos = getAllVideosInGroupInOrder(group);
        
        for (let i = 0; i < groupVideos.length; i++) {
            if (!completedVideos[groupVideos[i].id]) {
                return i;
            }
        }
        
        return groupVideos.length - 1;
    };
    
    const currentGroupIndex = getCurrentGroupIndex();
    const currentVideoIndexInGroup = getCurrentVideoIndexInGroup(currentGroupIndex);
    
    // Given a slug and level, returns the video ID string used in the sidebar/URL.
    // Mirrors the videoMap logic in Dashboard.js getVideoIdForExercise.
    const getVideoIdForSlugAndLevel = (slug, level) => {
        // Primitive reflex subgroup IDs use 'pr-levels-' for 1-3 and 'pr-level-' for 4-10
        const prGroup = (lvl) => lvl <= 3 ? `pr-levels-${lvl}` : `pr-level-${lvl}`;

        // Rooting & Snout are combined into a single item for levels 7+
        const prRootSnoutItem = (lvl) => lvl >= 7 ? `root-snout-${lvl}` : `root-${lvl}`;
        const prSnoutItem = (lvl) => lvl >= 7 ? `root-snout-${lvl}` : `snout-${lvl}`;

        const videoMap = {
            'palmergrasp':        { group: prGroup(level),              item: `pg-${level}` },
            'rooting':            { group: prGroup(level),              item: prRootSnoutItem(level) },
            'snout':              { group: prGroup(level),              item: prSnoutItem(level) },
            'babinski':           { group: prGroup(level),              item: `bab-${level}` },
            'spinalgalant':       { group: prGroup(level),              item: `sg-${level}` },
            'atnr':               { group: prGroup(level),              item: `atnr-${level}` },
            'stnr':               { group: prGroup(level),              item: `stnr-${level}` },
            'moro':               { group: prGroup(level),              item: `moro-${level}` },
            'tlr':                { group: prGroup(level),              item: `tlr-${level}` },
            'balance':            { group: 'balance',                   item: `bal-${level}` },
            'curlups':            { group: `core-level-${level}`,       item: `cu-${level}` },
            'lateralsideplank':   { group: `core-level-${level}`,       item: `lsp-${level}` },
            'pronebridge':        { group: `core-level-${level}`,       item: `pb-${level}` },
            'pullups':            { group: `core-level-${level}`,       item: `pu-${level}` },
            'pushups':            { group: `core-level-${level}`,       item: `psh-${level}` },
            'supinebridge':       { group: `core-level-${level}`,       item: `sb-${level}` },
            'oculomotor':         { group: 'oculomotor',                item: `ocul-${level}` },
            'olfactory':          { group: 'olfactory',                 item: `olf-${level}` },
            'vestibular':         { group: 'vestibular',                item: `vest-${level}` },
            'interactivemetronome': { group: 'interactive-metronome',   item: `int-metro-${level}` },
            'metrotimer':         { group: 'metrotimer',                item: `metro-${level}` },
            'transcranial':       { group: 'transcranial',              item: `trans-${level}` },
            'pons':               { group: 'pons',                      item: `pons-${level}` },
        };

        // Photobiomodulation: one video per level (levels 1-10)
        if (slug === 'photobiomodulation-left' || slug === 'photobiomodulation-right') {
            const prefix = slug === 'photobiomodulation-left' ? 'left' : 'right';
            const group  = slug === 'photobiomodulation-left' ? 'left-hemisphere' : 'right-hemisphere';
            if (level >= 1 && level <= 10) return `${group}-${prefix}-${level}`;
            return null;
        }

        const mapping = videoMap[slug];
        return mapping ? `${mapping.group}-${mapping.item}` : null;
    };

    // Helper function to get exercise slug and level from video ID
    const getExerciseFromVideoId = (videoId) => {
        const lowerVideoId = videoId.toLowerCase();
        
        const patterns = [
            { patterns: ['pg', 'palmergrasp'], slug: 'palmergrasp' },
            { patterns: ['root', 'rooting'], slug: 'rooting' },
            { patterns: ['snout'], slug: 'snout' },
            { patterns: ['bab', 'babinski'], slug: 'babinski' },
            { patterns: ['sg', 'spinalgalant', 'spinal'], slug: 'spinalgalant' },
            { patterns: ['atnr'], slug: 'atnr' },
            { patterns: ['stnr'], slug: 'stnr' },
            { patterns: ['moro'], slug: 'moro' },
            { patterns: ['tlr'], slug: 'tlr' },
            { patterns: ['balance', 'bal'], slug: 'balance' },
            { patterns: ['curlups', 'curl', '-cu-', '-cu'], slug: 'curlups' },
            { patterns: ['lateralsideplank', 'lateral', 'sideplank', '-lsp-', '-lsp'], slug: 'lateralsideplank' },
            { patterns: ['pronebridge', 'prone', '-pb-', '-pb'], slug: 'pronebridge' },
            { patterns: ['pullups', '-pu-', '-pu', 'pull'], slug: 'pullups' },
            { patterns: ['pushups', '-psh-', '-psh', 'push'], slug: 'pushups' },
            { patterns: ['supinebridge', 'supine', '-sb-', '-sb'], slug: 'supinebridge' },
            { patterns: ['oculomotor', 'ocular', 'ocul'], slug: 'oculomotor' },
            { patterns: ['olfactory', 'olfac'], slug: 'olfactory' },
            { patterns: ['vestibular', 'vest'], slug: 'vestibular' },
            { patterns: ['interactivemetronome', 'interactive', 'metronome'], slug: 'interactivemetronome' },
            { patterns: ['metrotimer', 'metro'], slug: 'metrotimer' },
            { patterns: ['left-hemisphere', 'left-'], slug: 'photobiomodulation-left' },
            { patterns: ['right-hemisphere', 'right-'], slug: 'photobiomodulation-right' },
            { patterns: ['pons-pons-', 'pons'], slug: 'pons' }
        ];
        
        let foundSlug = null;
        
        for (const { patterns: patternList, slug } of patterns) {
            for (const pattern of patternList) {
                if (lowerVideoId.includes(pattern)) {
                    foundSlug = slug;
                    break;
                }
            }
            if (foundSlug) break;
        }
        
        if (!foundSlug) {
            return null;
        }

        // For photobiomodulation, the video ID ends with the level number (e.g. "left-hemisphere-left-3").
        // Use a trailing-number match to avoid ambiguity from the repeated "left"/"right" words.
        if (foundSlug === 'photobiomodulation-left' || foundSlug === 'photobiomodulation-right') {
            const trailingMatch = videoId.match(/-(\d+)$/);
            const level = trailingMatch ? parseInt(trailingMatch[1]) : null;
            return { slug: foundSlug, level };
        }
        
        const levelMatch = videoId.match(/level-(\d+)|l(\d+)|-(\d+)(?:-|$)/i);
        const level = levelMatch ? parseInt(levelMatch[1] || levelMatch[2] || levelMatch[3]) : null;
        
        return { slug: foundSlug, level };
    };
    
    const isIntroVideo = (videoId, groupId) => {
        // Only the dedicated "Intro Videos" top-level group is always accessible.
        // Items inside other groups that happen to have "intro" in their name/id
        // (e.g. pr-intro-1, metro-intro, pons-intro) are NOT exempt.
        return groupId === 'intro-videos';
    };
    
    const itemMatchesLevel = (name, level) => {
        const levelPatterns = [
            new RegExp(`Level ${level}(?!\\d)`, 'i'),
            new RegExp(`L${level}(?!\\d)`, 'i'),
            new RegExp(`Level ${level - 1} & ${level}`, 'i'),
            new RegExp(`Level ${level} & ${level + 1}`, 'i'),
            new RegExp(`Level ${level - 2}-${level}`, 'i'),
            new RegExp(`Level ${level}-${level + 2}`, 'i')
        ];
        return levelPatterns.some(pattern => pattern.test(name));
    };
    
    const getAllVideosForLevel = (level) => {
        const levelVideos = [];
        videoGroups.forEach(group => {
            if (group.items) {
                group.items.forEach(item => {
                    if (itemMatchesLevel(item.name, level)) {
                        levelVideos.push(`${group.id}-${item.id}`);
                    }
                });
            }
            if (group.subgroups) {
                group.subgroups.forEach(subgroup => {
                    subgroup.items.forEach(item => {
                        if (itemMatchesLevel(item.name, level)) {
                            levelVideos.push(`${subgroup.id}-${item.id}`);
                        }
                    });
                });
            }
            if (group.additionalItems) {
                group.additionalItems.forEach(item => {
                    if (itemMatchesLevel(item.name, level)) {
                        levelVideos.push(`${group.id}-${item.id}`);
                    }
                });
            }
        });
        return levelVideos;
    };
    
    const calculateCurrentLevel = () => {
        let currentLevel = 1;
        for (let level = 1; level <= 10; level++) {
            const levelVideos = getAllVideosForLevel(level);
            const allCompleted = levelVideos.every(videoId => completedVideos[videoId]);
            if (allCompleted && levelVideos.length > 0) {
                currentLevel = level + 1;
            } else {
                break;
            }
        }
        return Math.min(currentLevel, 10);
    };
    
    const currentLevel = calculateCurrentLevel();
    
    const extractLevelNumber = (levelStr) => {
        const patterns = [
            /Level (\d+)/i,
            /L(\d+)/i,
            /Level (\d+) & \d+/i,
            /Level \d+ & (\d+)/i,
            /Level (\d+)-\d+/i
        ];
        for (const pattern of patterns) {
            const match = levelStr.match(pattern);
            if (match) return parseInt(match[1]);
        }
        return 0;
    };
    
    const toggleVideoCompletion = (videoId) => {
        const updated = {
            ...completedVideos,
            [videoId]: !completedVideos[videoId]
        };
        setCompletedVideos(updated);
        localStorage.setItem('completedVideos', JSON.stringify(updated));
    };
    
    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    // Returns true if a top-level group ID is always fully accessible regardless of focusedVideoId
    const isAlwaysAccessibleGroup = (groupId) => {
        return groupId === 'intro-videos' || groupId === 'sallie-instructions' || groupId === 'supplements';
    };

    // Returns true if a video item is accessible given the current focusedVideoId.
    // topLevelGroupId is the root group this item ultimately belongs to.
    const isItemAccessible = (itemId, groupId, topLevelGroupId) => {
        if (accessibleVideoIds === null) return true;
        if (isAlwaysAccessibleGroup(topLevelGroupId)) return true;
        const videoId = `${groupId}-${itemId}`;
        if (ALWAYS_ACCESSIBLE_VIDEO_IDS.has(videoId)) return true;
        if (accessibleVideoIds.has(videoId)) return true;
        return false;
    };

    // Returns true if a group (top-level) has at least one accessible video
    const groupHasAccessibleVideo = (group) => {
        if (accessibleVideoIds === null) return true;
        if (isAlwaysAccessibleGroup(group.id)) return true;

        if (group.items && group.items.some(item => isItemAccessible(item.id, group.id, group.id))) return true;
        if (group.additionalItems && group.additionalItems.some(item => isItemAccessible(item.id, group.id, group.id))) return true;
        if (group.subgroups) {
            for (const subgroup of group.subgroups) {
                if (subgroupHasAccessibleVideo(subgroup, group.id)) return true;
            }
        }
        return false;
    };

    // Returns true if a subgroup has at least one accessible video.
    // topLevelGroupId is the root group this subgroup belongs to.
    const subgroupHasAccessibleVideo = (subgroup, topLevelGroupId) => {
        if (accessibleVideoIds === null) return true;
        if (isAlwaysAccessibleGroup(topLevelGroupId)) return true;

        if (subgroup.items && subgroup.items.some(item => isItemAccessible(item.id, subgroup.id, topLevelGroupId))) return true;
        if (subgroup.subgroups) {
            for (const nested of subgroup.subgroups) {
                if (nested.items && nested.items.some(item => isItemAccessible(item.id, nested.id, topLevelGroupId))) return true;
            }
        }
        return false;
    };

    // Shared style for a fully-locked folder header
    const lockedFolderStyle = {
        opacity: 0.35,
        cursor: 'not-allowed',
        pointerEvents: 'none',
        userSelect: 'none',
    };

    const renderVideoItem = (item, groupId, groupIndex, topLevelGroupId) => {
        const videoId = `${groupId}-${item.id}`;
        const isCompleted = completedVideos[videoId];
        const isSelected = selectedVideo === videoId;
        
        const isLocked = !isItemAccessible(item.id, groupId, topLevelGroupId || groupId);
        
        const handleVideoClick = () => {
            if (isLocked) return; // Prevent clicking locked videos
            setSelectedVideo(videoId);
        };
        
        return (
            <div 
                key={videoId} 
                className={`checklist-item ${isSelected ? 'selected' : ''}`}
                onClick={handleVideoClick}
                style={{
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    opacity: isLocked ? 0.35 : 1,
                    pointerEvents: isLocked ? 'none' : 'auto',
                    userSelect: isLocked ? 'none' : 'auto',
                }}
            >
                <span className="video-label">
                    {translateExerciseName(item.name)}
                </span>
            </div>
        );
    };

    const getVideoUrl = (videoId) => {
        console.log('Getting video URL for videoId:', videoId);

        const buildIframeUrl = (raw) => {
            if (!raw || raw === '') return null;
            const match = raw.match(/cloudflarestream\.com\/([a-f0-9]+)/i);
            if (match && match[1]) {
                const uid = match[1];
                return `https://customer-hywi166rmnzn7jfo.cloudflarestream.com/${uid}/iframe?poster=https%3A%2F%2Fcustomer-hywi166rmnzn7jfo.cloudflarestream.com%2F${uid}%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600`;
            }
            if (raw.includes('/iframe')) return raw;
            return `https://customer-hywi166rmnzn7jfo.cloudflarestream.com/${raw}/iframe?poster=https%3A%2F%2Fcustomer-hywi166rmnzn7jfo.cloudflarestream.com%2F${raw}%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600`;
        };

        // Primary lookup: find the videoUrls group key that is a prefix of videoId
        for (const groupKey in videoUrls) {
            if (videoId.startsWith(groupKey + '-')) {
                const itemKey = videoId.substring(groupKey.length + 1);
                if (videoUrls[groupKey][itemKey] !== undefined) {
                    console.log(`Found via primary lookup — group: ${groupKey}, item: ${itemKey}`);
                    return buildIframeUrl(videoUrls[groupKey][itemKey]);
                }
            }
        }

        // Fallback: the videoId was built from a subgroup id (e.g. "staff-intro-bridget-intro")
        // whose URLs actually live under the parent group in videoUrls (e.g. "intro-videos").
        // Strip the subgroup prefix and search every videoUrls group for the item key.
        const firstDash = videoId.indexOf('-');
        if (firstDash !== -1) {
            // Try progressively longer prefixes until one matches a subgroup, then use the remainder
            // as the item key. We try every split point so multi-word subgroup ids work too.
            let remaining = videoId;
            while (remaining.includes('-')) {
                const splitAt = remaining.indexOf('-');
                remaining = remaining.substring(splitAt + 1);
                for (const groupKey in videoUrls) {
                    if (videoUrls[groupKey][remaining] !== undefined) {
                        console.log(`Found via subgroup fallback — group: ${groupKey}, item: ${remaining}`);
                        return buildIframeUrl(videoUrls[groupKey][remaining]);
                    }
                }
            }
        }

        console.log('No URL found for videoId:', videoId);
        return null;
    };

    return (
        <>  
            
            <Row>
                <Col md={3} className="video-sidebar-col">
                    <div className="video-sidebar-panel">
                        <div className="video-checklist-container">
                            {videoGroups.map((group, groupIndex) => {
                                const groupLocked = !groupHasAccessibleVideo(group);
                                return (
                                <div key={group.id} className="video-group" style={groupLocked ? lockedFolderStyle : {}}>
                                    <div 
                                        className="checklist-header"
                                        onClick={() => !groupLocked && toggleSection(group.id)}
                                        aria-controls={`${group.id}-collapse`}
                                        aria-expanded={expandedSections[group.id]}
                                        style={{ cursor: groupLocked ? 'not-allowed' : 'pointer' }}
                                    >
                                        <span className={`arrow ${expandedSections[group.id] ? 'expanded' : ''}`}>▶</span>
                                        <span>{getTranslatedGroupName(group.id, group.name)}</span>
                                    </div>
                                    
                                    <Collapse in={expandedSections[group.id]}>
                                        <div id={`${group.id}-collapse`} className="checklist-content">
                                            {group.items && group.items.map(item => renderVideoItem(item, group.id, groupIndex, group.id))}
                                            
                                            {group.subgroups && group.subgroups.map((subgroup) => {
                                                const subgroupLocked = !subgroupHasAccessibleVideo(subgroup, group.id);
                                                return (
                                                <div key={subgroup.id} className="nested-section" style={subgroupLocked ? lockedFolderStyle : {}}>
                                                    <div 
                                                        className="nested-header"
                                                        onClick={() => !subgroupLocked && toggleSection(subgroup.id)}
                                                        aria-controls={`${subgroup.id}-collapse`}
                                                        aria-expanded={expandedSections[subgroup.id]}
                                                        style={{ cursor: subgroupLocked ? 'not-allowed' : 'pointer' }}
                                                    >
                                                        <span className={`arrow ${expandedSections[subgroup.id] ? 'expanded' : ''}`}>▶</span>
                                                        <span>{getTranslatedGroupName(subgroup.id, subgroup.name)}</span>
                                                    </div>
                                                    
                                                    <Collapse in={expandedSections[subgroup.id]}>
                                                        <div id={`${subgroup.id}-collapse`} className="nested-content">
                                                            {subgroup.items && subgroup.items.map(item => renderVideoItem(item, subgroup.id, groupIndex, group.id))}
                                                            
                                                            {}
                                                            {subgroup.subgroups && subgroup.subgroups.map((nestedSubgroup) => {
                                                                const nestedLocked = accessibleVideoIds !== null && !isAlwaysAccessibleGroup(group.id) && !(
                                                                    nestedSubgroup.items && nestedSubgroup.items.some(item => isItemAccessible(item.id, nestedSubgroup.id, group.id))
                                                                );
                                                                return (
                                                                <div key={nestedSubgroup.id} className="nested-section" style={nestedLocked ? lockedFolderStyle : {}}>
                                                                    <div 
                                                                        className="nested-header"
                                                                        onClick={() => !nestedLocked && toggleSection(nestedSubgroup.id)}
                                                                        aria-controls={`${nestedSubgroup.id}-collapse`}
                                                                        aria-expanded={expandedSections[nestedSubgroup.id]}
                                                                        style={{ cursor: nestedLocked ? 'not-allowed' : 'pointer' }}
                                                                    >
                                                                        <span className={`arrow ${expandedSections[nestedSubgroup.id] ? 'expanded' : ''}`}>▶</span>
                                                                        <span>{getTranslatedGroupName(nestedSubgroup.id, nestedSubgroup.name)}</span>
                                                                    </div>
                                                                    
                                                                    <Collapse in={expandedSections[nestedSubgroup.id]}>
                                                                        <div id={`${nestedSubgroup.id}-collapse`} className="nested-content">
                                                                            {nestedSubgroup.items.map(item => renderVideoItem(item, nestedSubgroup.id, groupIndex, group.id))}
                                                                        </div>
                                                                    </Collapse>
                                                                </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </Collapse>
                                                </div>
                                                );
                                            })}
                                            
                                            {group.additionalItems && group.additionalItems.map(item => renderVideoItem(item, group.id, groupIndex, group.id))}
                                        </div>
                                    </Collapse>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                </Col>
                
                <Col md={9} className="video-content-col">
                    {selectedVideo ? (
                        (() => {
                            const videoUrl = getVideoUrl(selectedVideo);
                            const videoTitle = getVideoTitle(selectedVideo);
                            if (videoUrl) {
                                return (
                                    <div className="video-area">
                                        {videoTitle && (
                                            <h3 style={{
                                                marginBottom: '20px',
                                                color: '#333',
                                                fontSize: '24px',
                                                fontWeight: '600',
                                                paddingBottom: '10px'
                                            }}>
                                                {translateExerciseName(videoTitle)}
                                            </h3>
                                        )}
                                        <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                                            <iframe
                                                src={videoUrl}
                                                loading="lazy"
                                                style={{ 
                                                    border: 'none', 
                                                    position: 'absolute', 
                                                    top: 0, 
                                                    left: 0, 
                                                    height: '100%', 
                                                    width: '100%' 
                                                }}
                                                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                                                allowFullScreen={true}
                                            ></iframe>
                                        </div>
                                    </div>
                                );
                            } else {
                                return (
                                    <div className="video-area">
                                        {videoTitle && (
                                            <h3 style={{
                                                marginBottom: '20px',
                                                color: '#333',
                                                fontSize: '24px',
                                                fontWeight: '600',
                                                paddingBottom: '10px'
                                            }}>
                                                {translateExerciseName(videoTitle)}
                                            </h3>
                                        )}
                                        <div className="video-placeholder">
                                            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎥</div>
                                            <p className="video-instruction" style={{ color: '#333', fontSize: '18px', fontWeight: '600' }}>
                                                {t("video.ui.comingSoon")}
                                            </p>
                                            <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
                                                {t("video.ui.notAvailable")}
                                            </p>
                                        </div>
                                    </div>
                                );
                            }
                        })()
                    ) : (
                        <div className="video-area">
                            <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                                <iframe
                                    src="https://customer-hywi166rmnzn7jfo.cloudflarestream.com/07ac47868ac2e584dec22f0b9ed2955a/iframe?poster=https%3A%2F%2Fcustomer-hywi166rmnzn7jfo.cloudflarestream.com%2F07ac47868ac2e584dec22f0b9ed2955a%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
                                    loading="lazy"
                                    style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                                    allowFullScreen={true}
                                ></iframe>
                            </div>
                        </div>
                    )}
                </Col>
            </Row>
        </>
    );
};

export default Video;